import { useCallback, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

/* ------------------------------------------------------------------ */
/*  useSharePrescription (PRD.238)                                    */
/*                                                                    */
/*  Thin client for the `psharePrescriptionWhatsApp` Cloud Function. */
/*  Caller is responsible for producing the base64 image (typically  */
/*  via `renderNodeToPng` from `lib/prescription-image.ts`).         */
/* ------------------------------------------------------------------ */

const SHARE_URL = import.meta.env.VITE_WHATSAPP_PRESCRIPTION_SHARE_URL;

export type ShareStatus = 'idle' | 'rendering' | 'sending' | 'success' | 'error';

export interface SharePrescriptionInput {
  patientId: string;
  encounterId?: string;
  prescriptionIds?: string[];
  /** Base64 image (raw, no data URL prefix is fine — handler accepts both). */
  imageBase64: string;
  mimeType?: string;
  caption?: string;
  clientReference?: string;
}

export interface SharePrescriptionResult {
  jobId: string;
  deliveryId?: string;
  accepted?: boolean;
  providerMessageId?: string;
  mediaStoragePath?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface UseSharePrescriptionReturn {
  status: ShareStatus;
  error: string | null;
  lastResult: SharePrescriptionResult | null;
  enabled: boolean;
  send: (input: SharePrescriptionInput) => Promise<SharePrescriptionResult | null>;
  setRendering: () => void;
  reset: () => void;
}

export function useSharePrescription(): UseSharePrescriptionReturn {
  const [status, setStatus] = useState<ShareStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SharePrescriptionResult | null>(null);

  const enabled = Boolean(SHARE_URL);

  const setRendering = useCallback(() => {
    setStatus('rendering');
    setError(null);
  }, []);

  const send = useCallback(
    async (input: SharePrescriptionInput): Promise<SharePrescriptionResult | null> => {
      if (!SHARE_URL) {
        setError('Prescription share endpoint is not configured for this client.');
        setStatus('error');
        return null;
      }
      setStatus('sending');
      setError(null);
      try {
        const token = await getIdToken();
        const res = await fetch(SHARE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            patientId: input.patientId,
            encounterId: input.encounterId,
            prescriptionIds: input.prescriptionIds,
            imageBase64: input.imageBase64,
            mimeType: input.mimeType ?? 'image/png',
            caption: input.caption,
            clientReference: input.clientReference,
          }),
        });
        const body: unknown = await res.json().catch(() => null);

        if (!res.ok) {
          setError(friendlyError(res.status, body));
          setStatus('error');
          return null;
        }
        const result = body as SharePrescriptionResult;
        setLastResult(result);
        if (result.accepted) {
          setStatus('success');
        } else {
          setError(result.errorMessage ?? 'WhatsApp send failed.');
          setStatus('error');
        }
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to share prescription.';
        setError(msg);
        setStatus('error');
        return null;
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setLastResult(null);
  }, []);

  return { status, error, lastResult, enabled, send, setRendering, reset };
}

/* ------------------------------------------------------------------ */
/*  Error copy (PRD.238 §5)                                           */
/* ------------------------------------------------------------------ */

function extractError(body: unknown): string {
  if (typeof body === 'object' && body !== null && 'error' in body) {
    return String((body as { error?: unknown }).error ?? '');
  }
  return '';
}

function friendlyError(httpStatus: number, body: unknown): string {
  const raw = extractError(body);
  switch (httpStatus) {
    case 400:
      return raw || 'The prescription image could not be processed.';
    case 401:
      return 'Your session expired. Please sign in again.';
    case 403:
      // The handler already returns PRD-mandated copy for opted-out / no-inbound /
      // window-expired cases in `error` — just pass it through.
      return (
        raw ||
        'This patient is outside the WhatsApp 24-hour messaging window. Ask the patient to message the clinic first, then try again.'
      );
    case 501:
      return raw || 'The configured WhatsApp provider does not support media sends.';
    case 502:
      return (
        raw || 'We could not send the prescription via WhatsApp. Please try again or use Print.'
      );
    default:
      return (
        raw ||
        `We could not send the prescription via WhatsApp (HTTP ${httpStatus}). Please try again or use Print.`
      );
  }
}
